/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */
import React, { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { GraphRange, TouInterval } from "../../../lib/Types";
import { formatDateString } from "../Logic/formatUtils";
import {
  getTouTier,
  normalizeTouName,
  TOU_SEGMENT_BORDER,
  TOU_TIER_COLORS,
  TOU_TRACK_COLOR,
} from "./touConfig";

interface TouTimelineProps {
  intervals: TouInterval[];
  displayRange: GraphRange;
  leftMargin: number;
  rightMargin: number;
  timezone?: string;
}

const LABEL_GAP_PX = 8;

const TouTimeline: React.FC<TouTimelineProps> = ({
  intervals,
  displayRange,
  leftMargin,
  rightMargin,
  timezone,
}) => {
  const theme = useTheme();

  const segments = useMemo(() => {
    if (!intervals?.length) return [];
    const rangeStart = new Date(displayRange.start).getTime();
    const rangeStop = new Date(displayRange.stop).getTime();
    const totalSpan = rangeStop - rangeStart;
    if (totalSpan <= 0) return [];

    return intervals
      .map((interval, idx) => {
        const fromMs = new Date(interval.fromDateTime).getTime();
        const toMs = new Date(interval.toDateTime).getTime();
        const startMs = Math.max(fromMs, rangeStart);
        const endMs = Math.min(toMs, rangeStop);
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
        if (endMs <= startMs) return null;

        const leftPct = ((startMs - rangeStart) / totalSpan) * 100;
        const widthPct = ((endMs - startMs) / totalSpan) * 100;
        const tier = getTouTier(interval.touName);

        return {
          key: `${interval.touId ?? "tou"}-${idx}-${startMs}`,
          leftPct,
          widthPct,
          tier,
          name: normalizeTouName(interval.touName) || "Unknown",
          startIso: new Date(startMs).toISOString(),
          endIso: new Date(endMs).toISOString(),
        };
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);
  }, [intervals, displayRange.start, displayRange.stop]);

  if (!segments.length) return null;

  const labelWidth = Math.max(0, leftMargin - LABEL_GAP_PX);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        mb: 1,
      }}
    >
      <Box
        sx={{
          width: `${labelWidth}px`,
          paddingRight: `${LABEL_GAP_PX}px`,
          boxSizing: "border-box",
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 500,
          }}
        >
          Time of Use
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          paddingRight: `${rightMargin}px`,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 16,
            backgroundColor: TOU_TRACK_COLOR,
            borderRadius: `${theme.shape.borderRadius}px`,
          }}
        >
          {segments.map((segment) => (
            <Tooltip
              key={segment.key}
              title={
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {segment.name}
                  </Typography>
                  <Typography variant="caption" component="div">
                    {formatDateString(segment.startIso, true, timezone)}
                    {" – "}
                    {formatDateString(segment.endIso, true, timezone)}
                  </Typography>
                </Box>
              }
              placement="top"
              arrow
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: `${segment.leftPct}%`,
                  width: `${segment.widthPct}%`,
                  backgroundColor: TOU_TIER_COLORS[segment.tier],
                  border: `1px solid ${TOU_SEGMENT_BORDER}`,
                  borderRadius: `${theme.shape.borderRadius}px`,
                  boxSizing: "border-box",
                  cursor: "pointer",
                  transition: "filter 120ms ease-in-out",
                  "&:hover": {
                    filter: "brightness(1.05)",
                  },
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TouTimeline;
